import { useState, type FormEvent } from "react";
import AppleLogo from "../assets/apple.svg";

type DownloadEmailFormProps = {
    downloadUrl: string;
    buttonLabel?: string;
    className?: string;
    placeholderWhite?: boolean
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DownloadEmailForm = ({ downloadUrl, buttonLabel = "Download for Mac", className = "", placeholderWhite }: DownloadEmailFormProps) => {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError(null);
        setMessage(null);

        if (email.length > 0 && !EMAIL_REGEX.test(email)) {
            setError("Please enter a valid email address.");
            return;
        }

        const token = import.meta.env.VITE_AIRTABLE_TOKEN;

        if (!token) {
            setError("Email capture is not configured yet.");
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(`https://api.airtable.com/v0/app4z3Yvd7oYYZA7R/Emails`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    records: [
                        {
                            fields: {
                                Email: email,
                            },
                        },
                    ],
                }),
            });

            if (!response.ok) {
                // console.log(response.json())
                const text = await response.text();
                throw new Error(text || "Unable to save email");
                
            }

            setMessage("Starting your download…");
            window.location.href = downloadUrl;
        } catch (err) {
            console.error("Error saving email to Airtable", err);
            setError("Could not save your email. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className={`w-full max-w-xl flex flex-col sm:flex-row items-stretch sm:items-center gap-3 ${className}`}
        >
            <div className="flex flex-col gap-2">
                {error && <p className="ml-20 self-start text-sm text-red-500">{error}</p>}
                {message && <p className="ml-2 self-start text-sm text-green-600">{message}</p>} 
                <div className="flex items-center gap-2 min-w-140">
                    <input
                        type="email"
                        name="email"
                        placeholder="Enter your email for early access"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={submitting}
                        className={`flex-1 rounded-xl border border-gray-200 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-800 disabled:bg-gray-100 ${placeholderWhite ? "placeholder-white" : "placeholder-gray-400"}`}
                    />
                    <button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center justify-center space-x-2 bg-[#1A1A1A] text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:bg-black hover:shadow-xl transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap">
                    <img src={AppleLogo} alt="Apple Logo" className="w-5 h-5" />
                    <span>{submitting ? "Sending…" : buttonLabel}</span>
                </button>
                </div>           
            </div>
            
        </form>
    );
};

export default DownloadEmailForm;

